import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BellRing,
  ChevronRight,
  Loader2,
  MessageSquare,
  Minus,
  Moon,
  Plus,
  SlidersHorizontal,
  Users,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useUser } from '../../../contexts/UserContext';
import { useSaveTeamSettings, useTeamSettings } from '../../../services/onboardingBoard';

// Team settings for managers (Nic 2026-09-28): a small popover, not a page.
// Since 2026-10-23 (Nic: "keep it simple for Jorge") the managers keep only
// Assignment here (and Sales' Schedule and Call guide tools); the automated
// texts, the auto stall days, Messages and the Cadence Editor are the
// admins', on Admin > Automations. The text-switch rendering below stays for
// when a section with a textKey comes back.
// One menu per page (Nic 2026-10-07): the Sales board's covers the whole
// board (every tab), with its manager tools (Schedule, Cadence editor, Call
// guide) and the Signing Up settings; Client Onboarding's has only its own.
// Each setting is its own clearly framed
// section (white card, bold header, icon) on a tinted panel, so it is plain
// what belongs to what. Each is editable only by that team's manager (the
// server checks too). The words of every text (the drafts and the automated
// texts) are written on the Messages page since 2026-10-22; this menu keeps
// the on/off switches and links there.

const TEAMS = {
  sales: {
    title: 'Sales settings',
    sections: [
      {
        // How arriving leads are handed out (moved here from the Schedule
        // calendar, Nic 2026-10-07).
        kind: 'assign',
        title: 'Assignment',
        detail: 'Who gets each new lead. Anyone away is skipped.',
        assignKey: 'assignmentMode',
        modes: [
          { value: 'balanced', label: 'Balanced', hint: 'Whoever has the fewest open leads right now.' },
          { value: 'round_robin', label: 'Round robin', hint: 'Strict turns, in rotation order.' },
        ],
      },
    ],
  },
  onboarding: {
    title: 'Onboarding Workspace settings',
    sections: [
      {
        // How new clients are handed out (Nic 2026-10-05: no Unassigned).
        kind: 'assign',
        title: 'Assignment',
        detail: 'Who gets each newly signed client. A live hot seat goes to whoever the rep picks.',
        assignKey: 'onboardingAssignmentMode',
      },
    ],
  },
};
const ICON = { assign: Users, nurture: Moon, busy: BellRing };
const ASSIGN_MODES = [
  { value: 'balanced', label: 'Balanced', hint: 'Whoever has the fewest open clients.' },
  { value: 'round_robin', label: 'Round robin', hint: 'Strict turns, one each.' },
];
const POP_WIDTH = 368; // 23rem
const GUTTER = 16;

const Stepper = ({ value, min, max, disabled, onChange }) => (
  <div className="inline-flex shrink-0 items-center rounded-lg bg-white ring-1 ring-slate-200 dark:!bg-white/5 dark:ring-white/10">
    <button
      type="button"
      aria-label="Fewer days"
      disabled={disabled || value <= min}
      onClick={() => onChange(value - 1)}
      className="flex h-7 w-7 items-center justify-center rounded-l-lg text-slate-500 outline-none hover:bg-slate-100 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
    >
      <Minus size={13} />
    </button>
    <span className="w-8 text-center text-sm font-bold tabular-nums text-slate-800 dark:text-slate-100">
      {value}
    </span>
    <button
      type="button"
      aria-label="More days"
      disabled={disabled || value >= max}
      onClick={() => onChange(value + 1)}
      className="flex h-7 w-7 items-center justify-center rounded-r-lg text-slate-500 outline-none hover:bg-slate-100 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
    >
      <Plus size={13} />
    </button>
  </div>
);

const Switch = ({ on, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    // Fixed track + explicit knob offsets: Bootstrap's button padding and
    // box model otherwise push the knob past the right edge.
    className="shrink-0 border-0 bg-transparent p-0 outline-none focus:outline-none disabled:opacity-40"
  >
    <span
      className={cn(
        'relative block rounded-full transition-colors',
        on ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-white/15',
      )}
      style={{ width: 36, height: 20 }}
    >
      <span
        className="absolute rounded-full bg-white shadow transition-[left] duration-150"
        style={{ width: 16, height: 16, top: 2, left: on ? 18 : 2 }}
      />
    </span>
  </button>
);

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// tools: [{ key, label, detail, icon, onClick }] - this page's manager tools,
// opened from the same menu.
const TeamSettingsButton = ({ className = '', team = 'sales', tools = [] }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState(null); // { top, left, width, maxHeight }
  const q = useTeamSettings({ enabled: open });
  const save = useSaveTeamSettings();
  const [draft, setDraft] = useState({});
  const config = TEAMS[team] || TEAMS.sales;
  // The words live on the Messages page (admins and the Onboarding Manager).
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const canMessages = hasPermission('messages:manage');
  // Straight to that text's message (Nic 2026-10-23), not just the page.
  const MESSAGE_OF = { potentialNurtureText: 'auto_nurture', allBusyText: 'auto_all_busy', onboardingNurtureText: 'auto_stalled' };
  const openMessages = (textKey) => {
    setOpen(false);
    // The menu's own team's tab otherwise (Onboarding opens on Onboarding).
    const page = team === 'onboarding' ? '/operations/messages?team=onboarding' : '/operations/messages';
    navigate(MESSAGE_OF[textKey] ? `/operations/messages?key=${MESSAGE_OF[textKey]}` : page);
  };

  useEffect(() => {
    if (q.data?.settings) setDraft(q.data.settings);
  }, [q.data]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Placed from where the button actually is, not from a breakpoint (the
  // header wraps differently at every width): under the button, right edges
  // lined up, then clamped so it never leaves the window. Portaled to body so
  // no transformed parent can shift it. Same idea as Floating UI / Radix.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const place = () => {
      const b = btnRef.current?.getBoundingClientRect();
      if (!b) return;
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const width = Math.min(POP_WIDTH, vw - GUTTER * 2);
      const left = Math.min(Math.max(GUTTER, b.right - width), vw - width - GUTTER);
      const top = b.bottom + 8;
      setPos({ top, left, width, maxHeight: Math.max(160, vh - top - GUTTER) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Only this page's keys count as changes.
  const myKeys = config.sections.flatMap((sec) =>
    [sec.dayKey, sec.textKey, sec.assignKey].filter(Boolean),
  );
  const saved = q.data?.settings || {};
  const changedKeys = myKeys.filter((k) => q.data?.canEdit?.[k] && !same(draft[k], saved[k]));
  const setText = (key, patch) =>
    setDraft((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));

  const doSave = async () => {
    const patch = Object.fromEntries(changedKeys.map((k) => [k, draft[k]]));
    try {
      await save.mutateAsync(patch);
      toast.success('Settings saved.');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "Settings couldn't be saved.");
    }
  };

  const renderSection = (s, key) => {
    const Icon = ICON[s.kind] || SlidersHorizontal;
    const canDay = !!s.dayKey && !!q.data.canEdit?.[s.dayKey];
    const canText = !!s.textKey && !!q.data.canEdit?.[s.textKey];
    const canAssign = !!s.assignKey && !!q.data.canEdit?.[s.assignKey];
    const editable = canDay || canText || canAssign;
    const text = (s.textKey && draft[s.textKey]) || { enabled: false, text: '' };
    const mode = (s.assignKey && draft[s.assignKey]) || 'balanced';
    const modes = s.modes || ASSIGN_MODES;
    return (
      <section
        key={key}
        className={cn(
          'rounded-xl bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-slate-200 dark:!bg-surface-card dark:ring-white/10',
          !editable && 'opacity-60',
        )}
      >
        <div className="flex items-start gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:!bg-indigo-500/15 dark:text-indigo-300">
            <Icon size={14} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-bold text-slate-800 dark:text-slate-100">{s.title}</div>
            <div className="text-[11px] leading-snug text-slate-500 dark:text-slate-400">{s.detail}</div>
          </div>
          {s.dayKey ? (
            <Stepper
              value={Number(draft[s.dayKey]) || s.min}
              min={s.min}
              max={s.max}
              disabled={!canDay || save.isLoading}
              onChange={(v) => setDraft((d) => ({ ...d, [s.dayKey]: v }))}
            />
          ) : null}
        </div>

        {s.assignKey ? (
          <div className="mt-3">
            <div role="radiogroup" className="flex gap-1 rounded-lg bg-slate-100 p-0.5 dark:!bg-white/10">
              {modes.map((m) => {
                const on = mode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={!canAssign || save.isLoading}
                    onClick={() => setDraft((d) => ({ ...d, [s.assignKey]: m.value }))}
                    className={cn(
                      'h-7 flex-1 whitespace-nowrap rounded-md text-[12px] font-semibold outline-none focus:outline-none disabled:opacity-50',
                      on
                        ? 'bg-white text-slate-800 shadow-sm dark:!bg-surface-elevated dark:text-slate-100'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                    )}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              {modes.find((m) => m.value === mode)?.hint}
            </div>
          </div>
        ) : null}

        {s.textKey ? (
          <>
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-2 dark:!bg-white/[0.04]">
              <MessageSquare size={12} className="shrink-0 text-slate-400" />
              <span className="flex-1 text-[12px] font-medium text-slate-600 dark:text-slate-300">
                {s.textToggle || 'Text them when they move'}
              </span>
              {canMessages ? (
                <button
                  type="button"
                  onClick={() => openMessages(s.textKey)}
                  title="Its words are a message"
                  className="whitespace-nowrap text-[11px] font-semibold text-indigo-600 outline-none hover:text-indigo-500 focus:outline-none dark:text-indigo-300"
                >
                  Edit words
                </button>
              ) : null}
              <Switch
                on={!!text.enabled}
                disabled={!canText || save.isLoading}
                label={s.switchLabel || `Text ${s.textLabel} when they move to ${s.stallWord || 'Nurture'}`}
                onChange={(v) => setText(s.textKey, { enabled: v })}
              />
            </div>
          </>
        ) : null}
      </section>
    );
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={config.title}
        aria-label={config.title}
        // Icon only on a phone, like Everyone (2026-09-28). px-[..], not
        // px-0 / px-3.5: Bootstrap's px-0 is !important and wins at every size.
        className="inline-flex h-8 w-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-white px-[0px] text-[12px] sm:w-auto sm:px-[14px] font-medium text-slate-600 outline-none shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/80 hover:bg-slate-50 focus:outline-none dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10"
      >
        <SlidersHorizontal size={13} /> <span className="os-act-label hidden sm:inline">Settings</span>
      </button>
      {open && pos ? createPortal(
        <div
          ref={popRef}
          // A tinted panel so each white section stands out on it.
          className="fixed z-[1060] overflow-y-auto rounded-2xl bg-slate-50 shadow-[0_10px_40px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/80 dark:!bg-surface-page dark:ring-white/10"
          style={pos}
        >
          <div className="border-b border-slate-200/80 bg-white px-4 py-3 dark:border-white/10 dark:!bg-surface-elevated">
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{config.title}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Only this team&apos;s manager can change these.
            </div>
          </div>
          {q.isError ? (
            // A failed load says so instead of spinning forever (QA 2026-10-13).
            <div className="flex h-24 items-center justify-center px-[16px] text-center text-xs font-semibold text-rose-500">
              {q.error?.response?.data?.msg || "Settings couldn't be loaded. Close and try again."}
            </div>
          ) : q.isLoading || !q.data ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 size={16} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-3 p-3">
              {tools.length ? (
                // The page's manager tools, each opening its own screen.
                <section className="overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-slate-200 dark:!bg-surface-card dark:ring-white/10">
                  {tools.map((t, i) => {
                    const Icon = t.icon || SlidersHorizontal;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          t.onClick();
                        }}
                        className={cn(
                          'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left outline-none hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50 dark:hover:!bg-white/5 dark:focus-visible:!bg-white/5',
                          i > 0 && 'border-t border-slate-100 dark:border-white/[0.06]',
                        )}
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:!bg-indigo-500/15 dark:text-indigo-300">
                          <Icon size={14} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-bold text-slate-800 dark:text-slate-100">
                            {t.label}
                          </span>
                          {t.detail ? (
                            <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400">
                              {t.detail}
                            </span>
                          ) : null}
                        </span>
                        <ChevronRight size={14} className="shrink-0 text-slate-300 dark:text-slate-500" />
                      </button>
                    );
                  })}
                </section>
              ) : null}
              {config.sections.map((sec) => renderSection(sec, sec.title))}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setDraft(saved);
                    setOpen(false);
                  }}
                  className="h-8 whitespace-nowrap rounded-lg px-3 text-xs font-semibold text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none dark:text-slate-400 dark:hover:!bg-white/10"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!changedKeys.length || save.isLoading}
                  onClick={doSave}
                  className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-slate-900 px-3.5 text-xs font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:!bg-slate-100 dark:!text-slate-900"
                >
                  {save.isLoading ? <Loader2 size={12} className="animate-spin" /> : null}
                  Save
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body,
      ) : null}
    </div>
  );
};

export default TeamSettingsButton;
